import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import React, { useEffect, useState } from "react";

import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useAppSelector } from "@/hooks/redux";
import documentsService, {
  Document,
  RequiredDocument,
} from "@/services/documentsService";

import CourierApplicationCard from "./cards/CourierApplicationCard";
import FleetApplicationCard from "./cards/FleetApplicationCard";
import RestaurantApplicationCard from "./cards/RestaurantApplicationCard";

const BusinessDocuments: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    [key: string]: boolean;
  }>({});
  const [isUploading, setIsUploading] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [requiredDocuments, setRequiredDocuments] = useState<
    RequiredDocument[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);
  const [activeTab, setActiveTab] = useState<"documents" | "info">("info");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadDocuments();
    loadRequiredDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentsService.getUserDocuments();
      if (response.documents && Array.isArray(response.documents)) {
        setDocuments(response.documents);
        const uploadedState: { [key: string]: boolean } = {};
        response.documents.forEach((doc) => {
          const backendType = doc.document_type || doc.type;
          const filename = doc.original_filename || doc.name;
          const frontendType = documentsService.mapBackendToFrontendType(
            backendType,
            doc.description,
            filename,
          );
          uploadedState[frontendType] = true;
        });
        setUploadedDocuments(uploadedState);
      } else {
        setDocuments([]);
        setUploadedDocuments({});
      }
    } catch (err) {
      setError("Failed to load documents. Please try again.");
      setDocuments([]);
      setUploadedDocuments({});
    } finally {
      setLoading(false);
    }
  };

  const loadRequiredDocuments = async () => {
    try {
      const response = await documentsService.getRequiredDocuments();
      if (
        response.required_documents &&
        Array.isArray(response.required_documents)
      ) {
        setRequiredDocuments(response.required_documents);
      } else if (response.success && response.required_documents) {
        setRequiredDocuments(response.required_documents);
      }
    } catch (err) {
      // Optionally handle error
    }
  };

  // Upload, replace, view, download, delete handlers would be similar to drivers/Documents.tsx

  const renderBusinessDocumentCard = () => {
    if (!user?.role) return null;
    switch (user.role) {
      case "CourierOwner":
        return (
          <CourierApplicationCard
            isSubmitting={isUploading}
            // Add props for upload logic as needed
          />
        );
      case "FleetOwner":
        return (
          <FleetApplicationCard
            isSubmitting={isUploading}
            // Add props for upload logic as needed
          />
        );
      case "RestaurantOwner":
        return (
          <RestaurantApplicationCard
            isSubmitting={isUploading}
            // Add props for upload logic as needed
          />
        );
      default:
        return (
          <div className="rounded-lg bg-white p-6 text-center shadow-lg dark:bg-gray-800">
            <p className="text-gray-600 dark:text-gray-400">
              Please complete your profile to see document requirements.
            </p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 dark:bg-gray-900 sm:p-6 lg:p-8">
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <div className="flex-1">
              <div className="mb-1 h-7 w-64 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 sm:mb-2 sm:h-8 lg:h-10"></div>
              <div className="h-4 w-80 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 sm:h-5 sm:w-96"></div>
            </div>
            <div className="h-8 w-24 animate-pulse self-start rounded-md bg-gray-200 dark:bg-gray-700 sm:h-10 sm:w-28 sm:self-auto"></div>
          </div>
        </div>

        {/* Tab Menu Shimmer */}
        <div className="mb-6 flex border-b border-gray-200 dark:border-gray-700 sm:mb-8">
          <div className="h-9 w-28 animate-pulse rounded-t-lg bg-gray-200 dark:bg-gray-700 sm:h-10 sm:w-32"></div>
          <div className="ml-3 h-9 w-36 animate-pulse rounded-t-lg bg-gray-200 dark:bg-gray-700 sm:ml-4 sm:h-10 sm:w-40"></div>
        </div>

        {/* Content Shimmer */}
        <div className="mb-8 space-y-6">
          {/* Application Card Shimmer */}
          <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
            <div className="mb-6 h-7 w-48 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[...Array(6)].map((_, i) => (
                <div key={i}>
                  <div className="mb-2 h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Documents List Shimmer */}
          <div className="rounded-lg bg-white shadow-md dark:bg-gray-800">
            <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
              <div className="h-6 w-40 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {[...Array(3)].map((_, i) => (
                <div
                  className="flex flex-col space-y-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:p-6"
                  key={i}
                >
                  <div className="flex items-center">
                    <div className="mr-3 h-12 w-12 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700 sm:mr-4"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-5 w-48 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                      <div className="h-4 w-64 animate-pulse rounded bg-gray-200 dark:bg-gray-700"></div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700"></div>
                    <div className="flex space-x-2">
                      {[...Array(4)].map((_, j) => (
                        <div
                          className="h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
                          key={j}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 dark:bg-gray-900 sm:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
          <div>
            <h1 className="mb-1 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:mb-2 sm:text-3xl lg:text-4xl">
              Business Application
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
              Complete your business information and upload required documents.
            </p>
          </div>
          <div className="self-start sm:self-auto">
            <div className="flex space-x-2">
              <button
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 sm:px-4 sm:py-2 sm:text-sm"
                disabled={loading}
                onClick={() => loadDocuments()}
              >
                <RefreshCw
                  className={`mr-1.5 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Menu */}
      <div className="mb-4 flex overflow-x-auto border-b border-gray-200 dark:border-gray-700 sm:mb-6">
        <button
          className={`whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors duration-150 focus:outline-none sm:px-4 sm:text-sm ${
            activeTab === "info"
              ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400"
              : "text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300"
          }`}
          onClick={() => setActiveTab("info")}
        >
          Business Info
        </button>
        <button
          className={`ml-3 whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors duration-150 focus:outline-none sm:ml-4 sm:px-4 sm:text-sm ${
            activeTab === "documents"
              ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400"
              : "text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300"
          }`}
          onClick={() => setActiveTab("documents")}
        >
          Business Documents
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "info" && (
        <div className="mb-6 sm:mb-8">
          {/* Show only business info form, no documents */}
          {user?.role === "RestaurantOwner" && (
            <RestaurantApplicationCard showDocuments={false} />
          )}
          {user?.role === "FleetOwner" && (
            <FleetApplicationCard showDocuments={false} />
          )}
          {user?.role === "CourierOwner" && (
            <CourierApplicationCard showDocuments={false} />
          )}
        </div>
      )}
      {activeTab === "documents" && (
        <div className="mb-6 sm:mb-8">
          {/* Show only required documents upload section */}
          {user?.role === "RestaurantOwner" && (
            <RestaurantApplicationCard
              onDocumentUploaded={loadDocuments}
              showDocuments={true}
            />
          )}
          {user?.role === "FleetOwner" && (
            <FleetApplicationCard
              onDocumentUploaded={loadDocuments}
              showDocuments={true}
            />
          )}
          {user?.role === "CourierOwner" && (
            <CourierApplicationCard
              onDocumentUploaded={loadDocuments}
              showDocuments={true}
            />
          )}

          {/* Your Documents Management Section */}
          <div className="mt-6 rounded-lg bg-white shadow-md dark:bg-gray-800 sm:mt-8">
            <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg lg:text-xl">
                Your Documents
              </h2>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {documents.map((doc) => (
                <div
                  className="flex flex-col space-y-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:p-5 lg:p-6"
                  key={doc.id}
                >
                  <div className="flex items-start sm:items-center">
                    <div className="mr-3 flex-shrink-0 rounded-full bg-blue-100 p-2.5 text-blue-600 dark:bg-blue-900 dark:text-blue-400 sm:mr-4 sm:p-3">
                      {/* Icon */}
                      <svg
                        className="h-5 w-5 sm:h-6 sm:w-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M9 17v-2a2 2 0 012-2h2a2 2 0 012 2v2m-6 4h6a2 2 0 002-2V7a2 2 0 00-2-2h-1.5a1.5 1.5 0 01-3 0H9a2 2 0 00-2 2v12a2 2 0 002 2z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="break-words text-sm font-medium text-gray-900 dark:text-gray-100 sm:truncate sm:text-base lg:text-lg">
                        {doc.original_filename || doc.name}
                      </h3>
                      <div className="mt-1 flex flex-col space-y-0.5 text-xs text-gray-500 dark:text-gray-400 sm:flex-row sm:items-center sm:space-y-0 sm:text-sm">
                        <span>
                          Uploaded:{" "}
                          {new Date(
                            doc.uploaded_at || doc.uploadDate,
                          ).toLocaleDateString()}
                        </span>
                        {doc.expiryDate && (
                          <>
                            <span className="mx-2 hidden sm:inline">•</span>
                            <span>
                              Expires:{" "}
                              {new Date(doc.expiryDate).toLocaleDateString()}
                            </span>
                          </>
                        )}
                      </div>
                      {doc.description && (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 sm:mt-1">
                          {doc.description}
                        </p>
                      )}
                      {(doc.status === "rejected" ||
                        doc.status === "request-resubmission") &&
                        doc.admin_notes && (
                          <div className="mt-1 rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                            <strong>Admin message:</strong> {doc.admin_notes}
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                    <span
                      className={`inline-flex w-fit items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize sm:px-3 ${documentsService.getStatusColor(doc.status)}`}
                    >
                      {doc.status}
                    </span>
                    <div className="flex flex-wrap gap-2 sm:gap-0 sm:space-x-3">
                      <button
                        className="flex items-center gap-1 rounded bg-purple-50 px-2.5 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-100 hover:text-purple-900 dark:bg-purple-900/20 dark:text-purple-400 dark:hover:bg-purple-900/30 dark:hover:text-purple-300 sm:rounded-none sm:bg-transparent sm:px-0 sm:py-0 sm:text-sm sm:hover:bg-transparent sm:dark:bg-transparent sm:dark:hover:bg-transparent"
                        onClick={async () => {
                          try {
                            const blob =
                              await documentsService.downloadDocument(doc.id);
                            const url = window.URL.createObjectURL(blob);
                            window.open(url, "_blank");
                            setTimeout(
                              () => window.URL.revokeObjectURL(url),
                              1000,
                            );
                          } catch (error) {
                            setError(
                              "Failed to view document. Please try again.",
                            );
                          }
                        }}
                      >
                        View
                      </button>
                      <button
                        className="flex items-center gap-1 rounded bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100 hover:text-blue-900 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 dark:hover:text-blue-300 sm:rounded-none sm:bg-transparent sm:px-0 sm:py-0 sm:text-sm sm:hover:bg-transparent sm:dark:bg-transparent sm:dark:hover:bg-transparent"
                        onClick={async () => {
                          try {
                            const blob =
                              await documentsService.downloadDocument(doc.id);
                            const filename =
                              doc.original_filename || doc.name || "document";
                            documentsService.downloadFile(blob, filename);
                          } catch (error) {
                            setError(
                              "Failed to download document. Please try again.",
                            );
                          }
                        }}
                      >
                        Download
                      </button>
                      <button
                        className={`rounded px-2.5 py-1.5 text-xs font-medium sm:rounded-none sm:bg-transparent sm:px-0 sm:py-0 sm:text-sm sm:dark:bg-transparent ${
                          doc.status === "pending" ||
                          doc.status === "request-resubmission"
                            ? "cursor-pointer bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:bg-gray-700/50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:hover:bg-transparent sm:dark:hover:bg-transparent"
                            : "cursor-not-allowed bg-gray-100 text-gray-400 opacity-50 dark:bg-gray-800 dark:text-gray-600"
                        }`}
                        disabled={
                          doc.status !== "pending" &&
                          doc.status !== "request-resubmission"
                        }
                        onClick={async () => {
                          if (
                            doc.status !== "pending" &&
                            doc.status !== "request-resubmission"
                          )
                            return;

                          const input = window.document.createElement("input");
                          input.type = "file";
                          input.accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
                          input.onchange = async (e) => {
                            const file = (e.target as HTMLInputElement)
                              .files?.[0];
                            if (file) {
                              setIsUploading(true);
                              try {
                                const response =
                                  await documentsService.replaceDocument(
                                    doc.id,
                                    file,
                                    doc.description,
                                  );
                                if (response.document) {
                                  setDocuments((prev) =>
                                    prev.map((d) =>
                                      d.id === doc.id
                                        ? { ...d, ...response.document }
                                        : d,
                                    ),
                                  );
                                }
                              } catch (error) {
                                setError(
                                  "Failed to replace document. Please try again.",
                                );
                              } finally {
                                setIsUploading(false);
                              }
                            }
                          };
                          input.click();
                        }}
                        title={
                          doc.status === "pending" ||
                          doc.status === "request-resubmission"
                            ? "Replace document"
                            : "Replace is only available for pending or resubmission documents"
                        }
                      >
                        Replace
                      </button>
                      <button
                        className={`rounded px-2.5 py-1.5 text-xs font-medium sm:rounded-none sm:bg-transparent sm:px-0 sm:py-0 sm:text-sm sm:dark:bg-transparent ${doc.status === "pending" ? "cursor-pointer bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-900 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300 sm:hover:bg-transparent sm:dark:hover:bg-transparent" : "cursor-not-allowed bg-red-100 text-red-400 opacity-50 dark:bg-red-900/10 dark:text-red-600"}`}
                        disabled={doc.status !== "pending"}
                        onClick={() => {
                          if (doc.status !== "pending") return;
                          setDeletingDoc(doc);
                          setConfirmOpen(true);
                        }}
                        title={
                          doc.status !== "pending"
                            ? "Delete is only available for pending documents"
                            : "Delete document"
                        }
                      >
                        Delete
                      </button>
                      {/* ConfirmDialog for document deletion */}
                      <ConfirmDialog
                        cancelText="Cancel"
                        confirmText={deleting ? "Deleting..." : "Delete"}
                        message={`Are you sure you want to delete this document? This action cannot be undone.`}
                        onCancel={() => {
                          setConfirmOpen(false);
                          setDeletingDoc(null);
                        }}
                        onConfirm={async () => {
                          if (!deletingDoc) return;
                          setDeleting(true);
                          try {
                            const response =
                              await documentsService.deleteDocument(
                                deletingDoc.id,
                              );
                            if (response.success || response.message) {
                              setDocuments((prev) =>
                                prev.filter((d) => d.id !== deletingDoc.id),
                              );
                              setConfirmOpen(false);
                              setDeletingDoc(null);
                            }
                          } catch (error) {
                            setError(
                              "Failed to delete document. Please try again.",
                            );
                          } finally {
                            setDeleting(false);
                          }
                        }}
                        open={confirmOpen}
                        title="Delete Document"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 dark:bg-red-900/20 sm:mb-6 sm:p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <div className="flex items-start sm:items-center">
              <AlertCircle className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-red-500 sm:mt-0 sm:h-5 sm:w-5" />
              <p className="text-sm text-red-700 dark:text-red-400 sm:text-base">
                {error}
              </p>
            </div>
            <div className="flex items-center space-x-3 pl-6 sm:pl-0">
              <button
                className="text-xs text-red-600 underline hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 sm:text-sm"
                onClick={() => loadDocuments()}
              >
                Retry
              </button>
              <button
                className="text-lg font-semibold text-red-500 hover:text-red-700 sm:text-xl"
                onClick={() => setError(null)}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Documents List (optional, can be added like in drivers/Documents.tsx) */}
    </div>
  );
};

export default BusinessDocuments;
